/**
 * Every link that sends a visitor from the site to a store, and the one
 * place their campaign tagging is decided.
 *
 * TASK-644, implementing the contract in
 * `wiki/04-channels/web-to-store-attribution-baseline.md`. The baseline
 * found the reason the web→install question could not be answered: there
 * were no store links on the site at all except an `sameAs` URL inside
 * JSON-LD, which is for search engines and is not clickable. Nothing was
 * mistagged — there was nothing to tag. Links are born tagged here so the
 * first measurement of the channel is not lost the way the last one was.
 *
 * **`slot` is a cross-platform contract.** The same slot name is used on
 * both stores so the two funnels can be compared; adding one changes this
 * file and the wiki table together, the same rule the Adapty placement
 * registry follows.
 */

/** App Store id of the iOS app — one bundle, all locales. */
const APP_STORE_ID = '6748525000'

/** Play package of the Android app (`metro-android/app/build.gradle.kts`). */
const ANDROID_PACKAGE = 'com.mmthstudio.metro'

/**
 * App Store Connect provider token, read off ASC → App Analytics →
 * Acquisition → Campaigns on 2026-07-23.
 *
 * It identifies the publishing account, not the app, so the same value
 * works for every product in the portfolio. Not a secret — it ships in
 * public campaign URLs — but it cannot be derived, which is why this
 * constant sat at `null` until someone generated a campaign link: a `ct`
 * without a `pt` is ignored by the App Store, so a guessed value would
 * have produced links that look measured and attribute to nothing.
 */
const APP_STORE_PROVIDER_TOKEN = '122125305'

/**
 * One per placement. Same names on both platforms — a slot that exists on
 * one store and not the other makes the funnels incomparable, which is
 * the failure this registry exists to prevent.
 */
const SLOTS = Object.freeze({
  SMART_BANNER: 'web-smart-banner',
  HOME_CTA: 'web-home-cta',
  STATION_PAGE: 'web-station-page',
  LINE_PAGE: 'web-line-page',
  ROUTE_PAGE: 'web-route-page',
  // Line view on the map: the desktop side panel and the phone line card
  // are separate channels in the clicks report (Figma LineView legend).
  LINE_PANEL: 'web-line-panel',
  LINE_CARD: 'web-line-card',
  // The app prompt on entry (A/B, 2026-11-03): one slot per variant, so the
  // store-side campaign reports read each arm on its own.
  APP_PROMPT_HOME: 'web-app-prompt-home',
  APP_PROMPT_ROUTE_A: 'web-app-prompt-route-a',
  APP_PROMPT_ROUTE_B: 'web-app-prompt-route-b',
  APP_PROMPT_DESKTOP_1: 'web-app-prompt-desktop-1',
  APP_PROMPT_DESKTOP_2: 'web-app-prompt-desktop-2',
})

/**
 * Play Store URL with an arbitrary `utm_*` referrer — the building block of
 * `StoreLinks.googlePlay`, also used by the `/go/<source>` tracking links
 * (`trackingLinks.mjs`), whose source is the channel, not the site. A plain
 * function, not a method, so it keeps working when destructured.
 *
 * @param {{ source: string, medium: string, campaign: string }} utm
 */
function googlePlayReferred({ source, medium, campaign }) {
  const referrer = `utm_source=${source}&utm_medium=${medium}&utm_campaign=${campaign}`
  return (
    `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}` +
    `&referrer=${encodeURIComponent(referrer)}`
  )
}

export const StoreLinks = Object.freeze({
  APP_STORE_ID,
  ANDROID_PACKAGE,
  SLOTS,

  /** True when iOS links can carry campaign attribution. */
  get hasAppStoreCampaignToken() {
    return APP_STORE_PROVIDER_TOKEN !== null
  },

  /**
   * App Store Campaign Link.
   *
   * The `/app/apple-store/id…` path is the form App Store Connect's own
   * campaign generator emits; `/app/id…` resolves to the same listing but
   * is not what the attribution examples use, and this is not the place
   * to find out whether the difference matters.
   *
   * Falls back to an untagged URL if the provider token is ever cleared —
   * a `ct` without a `pt` is ignored by the App Store, so emitting one
   * alone would only make the link look measured.
   *
   * @param {string} slot one of {@link SLOTS}
   */
  appStore(slot) {
    const base = `https://apps.apple.com/app/apple-store/id${APP_STORE_ID}`
    if (APP_STORE_PROVIDER_TOKEN === null) return base
    return `${base}?pt=${APP_STORE_PROVIDER_TOKEN}&ct=${encodeURIComponent(slot)}&mt=8`
  },

  /**
   * Play Store URL carrying an install referrer. The referrer is a single
   * URL-encoded query string, so its `utm_*` pairs are encoded twice —
   * once inside `referrer`, once as part of the outer URL. Getting that
   * wrong is the usual reason Play referrer data arrives empty.
   *
   * @param {string} slot one of {@link SLOTS}
   */
  googlePlay(slot) {
    return googlePlayReferred({ source: 'london-tube.com', medium: 'web', campaign: slot })
  },

  /** See {@link googlePlayReferred} above. */
  googlePlayReferred,

  /**
   * `content` for `<meta name="apple-itunes-app">` — Safari's native
   * Smart App Banner. No design work involved and no layout of ours to
   * break: iOS renders it above the page.
   *
   * `app-argument` carries the URL the installed app should open — the
   * page's own canonical by default, or a deep-link form from
   * `appDeepLinks.mjs` when the canonical is not one the app resolves
   * (route pages, TASK-590). Omitted entirely when no URL is given: the
   * SPA shell `index.html` is a static file with no page context, and an
   * empty `app-argument=` would be worse than none. `affiliate-data`
   * appears only with a provider token, for the same reason as
   * {@link appStore}.
   *
   * @param {string} [appArgumentUrl] absolute URL the installed app opens
   */
  smartAppBannerContent(appArgumentUrl) {
    const parts = [`app-id=${APP_STORE_ID}`]
    if (APP_STORE_PROVIDER_TOKEN !== null) {
      parts.push(`affiliate-data=pt=${APP_STORE_PROVIDER_TOKEN}&ct=${SLOTS.SMART_BANNER}`)
    }
    if (appArgumentUrl !== undefined) {
      parts.push(`app-argument=${appArgumentUrl}`)
    }
    return parts.join(', ')
  },
})
